import { cva, type VariantProps } from 'class-variance-authority'
import { CustomerState, TicketFile } from 'lib/api/ticket-files/ticket-files'
import IconButton from 'lib/components/buttons/icon-button'
import Checkbox from 'lib/components/checkbox/checkbox'
import { getCloudfrontPreviewUrl, getPreviewUrl, isPlaceholder } from 'lib/util/file/file'
import {
  CircleCheckIcon,
  DownloadIcon,
  ExternalLinkIcon,
  ForwardIcon,
  InfoIcon,
  MoreVerticalIcon,
  PenLineIcon,
  TrashIcon,
  UserPlusIcon,
} from 'lucide-react'
import { useFilesContext } from './files-context'
import { cn } from 'lib/util/cn'
import ApprovedBadge from 'lib/components/approved-badge'
import FileType from 'lib/components/file-type/file-type'
import { useMemo, useState } from 'react'
import Tooltip from 'lib/components/tooltip/tooltip'
import CompanyTaggingPopoverTitle from '../company-tagging/company-tagging-popover-title'
import { Popover, PopoverContent, PopoverTrigger } from 'lib/components/popover/popover'
import { MenuItem } from 'lib/components/menu-item/menu-item'
import { FileExportModalMode } from './file-modal/file-modal'
import { useFeatureFlagsContext } from 'lib/components/feature-flags/feature-flags-provider'

export const thumbnailVariants = cva(
  // Base styles
  'tw-relative tw-rounded-lg tw-group tw-overflow-hidden tw-transition-all tw-duration-200 tw-border tw-border-solid tw-border-neutral-200 tw-shadow-sm tw-bg-neutral-100 tw-flex tw-flex-col tw-items-center tw-justify-center',
  {
    variants: {
      size: {
        sm: 'tw-w-20 tw-h-20',
        md: 'tw-w-48 tw-h-48',
        lg: 'tw-w-64 tw-h-64',
      },
      status: {
        preview: 'tw-bg-gray-900',
        unavailable: 'tw-bg-gray-100',
      },
      selected: {
        true: 'tw-ring-1 tw-ring-neutral-800',
      },
      focused: {
        true: 'tw-ring-2 !tw-ring-cornflower-500',
      },
      actions: {
        true: 'tw-cursor-pointer hover:tw-shadow-xl hover:tw-scale-[1.02]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

const badgeSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'tw-h-5 tw-w-5',
      md: 'tw-h-6 tw-w-6',
      lg: 'tw-h-6 tw-w-6',
    },
  },
})
const imagePlaceholderVariants = cva('', {
  variants: {
    size: {
      sm: 'tw-h-16 tw-w-16',
      md: 'tw-h-25 tw-w-25',
      lg: 'tw-h-25 tw-w-25',
    },
  },
})

export type FileThumbnailVariants = VariantProps<typeof thumbnailVariants>

export interface FileThumbnailProps extends FileThumbnailVariants {
  file: TicketFile
  canDelete?: boolean
  onRefetch?: () => void
  onClick?: () => void
  actions?: boolean
  checkable?: boolean
}

function PreviewOverlay() {
  return (
    <div
      className="tw-absolute tw-z-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-40"
      data-testid="preview-overlay"
    >
      <h4 className="tw-m-0 tw-text-white">PREVIEW</h4>
    </div>
  )
}

const FileThumbnail = ({
  file,
  size,
  status,
  onClick,
  focused,
  canDelete,
  actions = true,
  checkable = true,
}: FileThumbnailProps) => {
  const {
    isSelected,
    addSelectedFile,
    removeSelectedFile,
    setMode,
    setSelectedFile,
    deleteTicketFile,
    toggleApproved,
    setDrawerFileId,
  } = useFilesContext()

  const { isFeatureFlagEnabled } = useFeatureFlagsContext()
  const stockpressEnabled = isFeatureFlagEnabled('stockpress')
  const cloudfrontEnabled = isFeatureFlagEnabled('cloudfront')

  const [popoverOpen, setPopoverOpen] = useState(false)

  const { previewUrl, isPlaceholder: isPlaceholderImage } = cloudfrontEnabled
    ? getCloudfrontPreviewUrl({
        previewUrl: file.previewUrl,
        s3PreviewKey: file.s3PreviewKey,
      })
    : { previewUrl: getPreviewUrl(file.previewUrl), isPlaceholder: isPlaceholder(file.previewUrl) }

  const selected = isSelected(file)

  const isUploadedByCollaborator = file?.user?.collaborator === true

  const annotationsCount = useMemo(() => {
    if (file.isExtractable) {
      return file.extractedPages.reduce((acc, pdf) => acc + pdf.annotations.length, 0)
    } else {
      return file.annotations?.length || 0
    }
  }, [file.isExtractable, file.extractedPages, file.annotations?.length])

  return (
    <div
      className={thumbnailVariants({ size, status, selected, focused, actions })}
      onClick={onClick}
      data-testid="file-thumbnail"
    >
      <div className="tw-relative tw-flex tw-h-full tw-w-full tw-shrink tw-items-center tw-justify-center tw-overflow-hidden">
        <img
          src={previewUrl}
          alt={file.name}
          className={cn(
            'tw-h-full tw-w-full tw-object-cover',
            isPlaceholderImage && imagePlaceholderVariants({ size }),
          )}
        />
        {file.preview && <PreviewOverlay />}
        <div className="tw-absolute tw-bottom-1 tw-left-1 tw-z-20 tw-flex tw-flex-row tw-items-center tw-justify-center">
          {(!isPlaceholderImage || size === 'md' || size === 'lg') && (
            <Tooltip content="Format" side="top">
              <FileType className={cn(badgeSizeVariants({ size }))} fileName={file?.name} />
            </Tooltip>
          )}
          {file?.customerState === CustomerState.Approved && (
            <Tooltip content="Approved" side="top">
              <ApprovedBadge
                className={cn(badgeSizeVariants({ size }), {
                  '-tw-ml-1': !isPlaceholderImage,
                })}
              />
            </Tooltip>
          )}
        </div>

        <div className="tw-absolute tw-bottom-1 tw-right-1 tw-z-20 tw-flex tw-flex-row tw-items-center tw-justify-center">
          {isUploadedByCollaborator && (
            <Tooltip content={`Uploaded by ${file.user?.name || 'collaborator'}`} side="top">
              <div
                className={cn(
                  'tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-p-1',
                  badgeSizeVariants({ size }),
                )}
              >
                <UserPlusIcon />
              </div>
            </Tooltip>
          )}
          {annotationsCount > 0 && (
            <div
              className={cn(
                'tw-relative tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-p-1',
                isUploadedByCollaborator && '-tw-ml-1',
                badgeSizeVariants({ size }),
              )}
            >
              <PenLineIcon className="lu-light lu-sm" />
              <div className="tw-absolute tw-right-0 tw-top-0 tw-rounded-full tw-bg-orange-500 tw-px-0.5 tw-text-[8px] tw-text-white">
                {annotationsCount}
              </div>
            </div>
          )}
        </div>
      </div>
      {checkable && (
        <Checkbox
          id="file-thumbnail-checkbox"
          className={cn('tw-invisible tw-absolute tw-left-2 tw-top-2 group-hover:tw-visible', {
            'tw-visible': selected,
          })}
          isChecked={selected}
          onClick={(e) => {
            e.stopPropagation()
            if (selected) {
              removeSelectedFile(file)
            } else {
              addSelectedFile(file)
            }
          }}
        />
      )}

      {actions && (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <IconButton
              size={size === 'sm' ? '2xs' : 'xs'}
              color="secondary"
              onClick={() => {}}
              className="tw-invisible tw-absolute tw-right-1 tw-top-1 group-hover:tw-visible"
            >
              <MoreVerticalIcon />
            </IconButton>
          </PopoverTrigger>
          <PopoverContent intent="flyout" align="start">
            <MenuItem
              item={{ label: 'Add to...', Icon: ForwardIcon }}
              onClick={() => {
                setSelectedFile(file)
                setMode(FileExportModalMode.ADD_TO)
              }}
            />
            <MenuItem
              item={{ label: 'Download', Icon: DownloadIcon }}
              onClick={() => {
                setSelectedFile(file)
                setMode(FileExportModalMode.DOWNLOAD)
              }}
            />
            {stockpressEnabled && (
              <MenuItem
                item={{ label: 'Export', Icon: ExternalLinkIcon }}
                onClick={() => {
                  setSelectedFile(file)
                  setMode(FileExportModalMode.EXPORT)
                }}
              />
            )}
            <MenuItem
              item={{ label: 'View details', Icon: InfoIcon }}
              onClick={() => {
                setDrawerFileId(file.id)
              }}
            />
            {(canDelete || stockpressEnabled) && <hr className="tw-my-2 tw-w-full tw-border-neutral-200" />}
            {stockpressEnabled && (
              <MenuItem
                className={cn('tw-bg-neutral-200', {
                  'tw-border-picklegreen-200 tw-bg-picklegreen-100 tw-text-picklegreen-800 hover:tw-bg-picklegreen-200 disabled:tw-bg-picklegreen-100':
                    file.customerState === CustomerState.Approved,
                })}
                item={{
                  label: file.customerState === CustomerState.Approved ? 'Approved' : 'Mark approved',
                  Icon: CircleCheckIcon,
                }}
                onClick={() => {
                  toggleApproved(file)
                  setPopoverOpen(false)
                }}
              />
            )}
            {canDelete && (
              <MenuItem
                className="tw-text-red-500"
                item={{ label: 'Delete', Icon: TrashIcon }}
                onClick={() => {
                  deleteTicketFile(file)
                  setPopoverOpen(false)
                }}
              />
            )}
          </PopoverContent>
        </Popover>
      )}

      {size === 'lg' && (
        <div className="tw-flex tw-min-h-16 tw-w-full tw-shrink-0 tw-flex-col tw-gap-1 tw-bg-white tw-px-4 tw-py-3">
          <div className="tw-truncate tw-font-bold tw-text-neutral-800">{file.name}</div>
          <CompanyTaggingPopoverTitle selectedTags={file.tags} emptyTagTitleElement={null} maxCharacters={20} />
        </div>
      )}
    </div>
  )
}

export default FileThumbnail
